/**
 * Shared PDF / screenshot modal. Defaults to react-pdf {@link PdfRender}.
 * When Redux `metaData.useOasisToolbar` is true, renders
 * {@link OasisPdfViewerPanel} instead (radiology clinical documents).
 *
 * @module docViewerModal
 */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import DocumentFeedbackModal from "../../documentFeedback/DocumentFeedbackModal";
import { useDocumentFeedback } from "../../documentFeedback/useDocumentFeedback";
import OasisPdfViewerPanel from "../../oasisPdfToolbar/OasisPdfViewerPanel";
import PdfRender from "../../pdfRender/pdfRender";

interface DocViewerModalProps {
  /** Optional pre-resolved download URL; used when `metaData.filePath` is absent. */
  fileUrl?: string;
  /** Closes the modal. */
  onClose: () => void;
  type: "clinical_attachment" | "cmm_screenshot";
  shouldLogEvent?: boolean;
  screenName?: string;
  formName?: string;
}

/**
 * Fetches a signed URL from `metaData.filePath` (or uses `fileUrl`) and
 * renders the document in a downloadable modal. Clinical documents that use
 * the oasis toolbar also expose thumbs-up / thumbs-down feedback.
 *
 * @input props - File URL, close handler, document type, and optional analytics
 * @return The document viewer modal
 */
const DocViewerModal = ({
  fileUrl,
  onClose,
  type,
  shouldLogEvent,
  screenName,
  formName,
}: DocViewerModalProps) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const [currentFileUrl, setCurrentFileUrl] = useState<string>(fileUrl || "");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgID = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const clinicalDocument = generalOrderDetails?.order?.clinical_document;
  const useOasisToolbar = Boolean(metaData?.useOasisToolbar);
  const showDocumentFeedback =
    useOasisToolbar &&
    type === "clinical_attachment" &&
    (metaData?.title === "Clinical Document" ||
      screenName === ScreenNamesForBigQuery.FORM_SCREEN);
  // The page mapping indexes the merged clinical document only, so it must not
  // be shown for any other file (e.g. the radiology order document).
  const documentPageMapping =
    metaData?.filePath && metaData.filePath === clinicalDocument?.file_path
      ? clinicalDocument?.document_page_mapping
      : undefined;

  const isEventLogged = useRef(false);
  const {
    feedback,
    handleFeedback,
    isFeedbackModalOpen,
    submitNegativeFeedback,
    closeFeedbackModal,
  } = useDocumentFeedback({
    screenName: screenName ?? ScreenNamesForBigQuery.FORM_SCREEN,
  });

  useEffect(() => {
    if (shouldLogEvent && !isEventLogged.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: EventsName.MODAL_SHOWN,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          model_name:
            type === "clinical_attachment" ? "Clinical Document" : "Screenshot",
          screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
          payer_name: payerName,
        },
      });
      isEventLogged.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchFileUrl = async () => {
      if (!metaData?.filePath) return;

      setIsLoading(true);
      try {
        const downloadUrl = await getFileDownloadUrl(metaData.filePath);
        if (downloadUrl) {
          setCurrentFileUrl(downloadUrl);
        }
      } catch (error) {
        console.error("Error fetching file URL:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (metaData?.filePath) {
      setIsLoading(true);
      fetchFileUrl();
    } else if (fileUrl) {
      setCurrentFileUrl(fileUrl);
      setIsLoading(false);
    }
  }, [metaData?.filePath, fileUrl]);

  const handleDownload = () => {
    if (currentFileUrl) {
      window.open(currentFileUrl, "_blank");
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: EventsName.DOWNLOAD_BUTTON_CLICKED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: screenName ?? "",
          form_name: formName ?? "",
          payer_name: payerName,
        },
      });
      onClose();
    }
  };

  return (
    <>
      <Modal
        dialogId={ModalId.DOC_VIEWER_MODAL}
        onSave={handleDownload}
        title={
          metaData?.title
            ? metaData?.title
            : type === "clinical_attachment"
              ? "Clinical Document"
              : "Screenshot"
        }
        saveButtonText={"Download"}
        cancelText={"Cancel"}
        heightPercentage={90}
        onCancel={onClose}
        onClose={onClose}
        hideFooter={metaData?.hideFooter ?? type === "cmm_screenshot"}
      >
        <div
          className={`doc-viewer-modal--container h-full w-full ${
            useOasisToolbar ? "flex min-h-0 overflow-hidden" : "overflow-scroll"
          }`}
        >
          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center gap-4">
              <SpinningLoader />
              <p className="text-gray-600">Loading document...</p>
            </div>
          ) : currentFileUrl ? (
            useOasisToolbar ? (
              <OasisPdfViewerPanel
                pdfUrl={currentFileUrl}
                documentPageMapping={documentPageMapping}
                showFeedback={showDocumentFeedback}
                feedback={feedback}
                onFeedback={handleFeedback}
              />
            ) : (
              <PdfRender file={currentFileUrl} />
            )
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-gray-600">No document available</p>
            </div>
          )}
        </div>
      </Modal>
      {showDocumentFeedback && (
        <DocumentFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={closeFeedbackModal}
          onSubmit={submitNegativeFeedback}
        />
      )}
    </>
  );
};

export default DocViewerModal;
